<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-device-alarm-count-stats">
    <nx-report-header title="设备报警 · 数量统计与分类">
      <div class="dac-header-actions">
        <div class="dac-nav">
          <el-button type="text" @click="$router.push('/kanban-management/device-alarm-board')">报警看板</el-button>
          <el-button type="text" @click="$router.push('/kanban-management/device-alarm-chart-dynamic')">图表动态</el-button>
          <el-button type="text" @click="$router.push('/kanban-management/device-fault-analysis')">故障分析</el-button>
        </div>
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>


    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="报警时间">
        <el-date-picker
          v-model="dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始"
          end-placeholder="结束"
          value-format="timestamp"
          :default-time="['00:00:00', '23:59:59']"
          style="width: 340px"
          clearable
          @change="loadData"
        />
      </el-form-item>
      <el-form-item label="提醒级别">
        <el-select v-model="filters.alertType" clearable placeholder="全部" style="width: 110px" @change="loadData">
          <el-option v-for="l in alertTypeOptions" :key="l.value" :label="l.label" :value="l.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="报警类型">
        <el-select v-model="filters.type" clearable placeholder="全部" style="width: 120px" @change="loadData">
          <el-option v-for="t in typeOptions" :key="t.value" :label="t.label" :value="t.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="filters.alertStatus" clearable placeholder="全部" style="width: 110px" @change="loadData">
          <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="设备分类">
        <el-select v-model="filters.category" clearable placeholder="全部" style="width: 100px" @change="loadData">
          <el-option v-for="c in categoryOptions" :key="c.value" :label="c.label" :value="c.value" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="resetFilters">重置</el-button>
      </el-form-item>
    </el-form>

    <div class="dac-kpi-row">
      <div class="dac-kpi">
        <div class="lab">符合条件的记录数</div>
        <div class="val">{{ kpi.totalCount }}</div>
      </div>
      <div class="dac-kpi">
        <div class="lab">今日报警（条）</div>
        <div class="val muted">{{ kpi.todayCount }}</div>
      </div>
      <div class="dac-kpi">
        <div class="lab">未关闭（条）</div>
        <div class="val">{{ kpi.openCount }}</div>
      </div>
      <div class="dac-kpi">
        <div class="lab">一级且未关闭</div>
        <div class="val">{{ kpi.level1OpenCount }}</div>
      </div>
    </div>

    <div class="dac-grid-2">
      <div class="dac-card">
        <h3>按设备分类 · 数量与占比</h3>
        <bucket-table :rows="byCategory" :total="kpi.totalCount" />
      </div>
      <div class="dac-card">
        <h3>按报警类型 · 数量与占比</h3>
        <bucket-table :rows="byType" :total="kpi.totalCount" />
      </div>
    </div>

    <div class="dac-grid-2">
      <div class="dac-card">
        <h3>按提醒级别 · 数量</h3>
        <bucket-table :rows="byAlertType" :total="kpi.totalCount" />
      </div>
      <div class="dac-card">
        <h3>按处理状态 · 数量</h3>
        <bucket-table :rows="byStatus" :total="kpi.totalCount" />
      </div>
    </div>

    <div class="dac-card">
      <h3>交叉统计：设备分类 × 提醒级别</h3>
      <div class="dac-matrix-wrap">
        <table class="dac-matrix" v-if="matrix.rowKeys.length">
          <thead>
            <tr>
              <th class="corner">分类 \ 级别</th>
              <th v-for="c in matrix.colKeys" :key="c">{{ c }}</th>
              <th>行合计</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in matrix.rowKeys" :key="r">
              <th class="corner">{{ r }}</th>
              <td
                v-for="c in matrix.colKeys"
                :key="r + '-' + c"
                :class="cellClass(matrix.get(r, c))"
              >
                {{ matrix.get(r, c) }}
              </td>
              <td style="font-weight: 600">{{ matrix.rowSum(r) }}</td>
            </tr>
            <tr>
              <th class="corner">列合计</th>
              <td v-for="c in matrix.colKeys" :key="'col-' + c" style="font-weight: 600">
                {{ matrix.colSum(c) }}
              </td>
              <td style="font-weight: 700; background: #fef2f2">{{ kpi.totalCount }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else style="color: #94a3b8; padding: 24px; text-align: center">暂无数据</div>
      </div>
    </div>
  </div>
</template>

<script>
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { getDictionary } from '@/api/system/dictbiz'
import { fetchDeviceAlarmCountStats } from './api'

const BucketTable = {
  functional: true,
  props: {
    rows: { type: Array, default: () => [] },
    total: { type: Number, default: 0 }
  },
  render(h, ctx) {
    const rows = ctx.props.rows || []
    const total = ctx.props.total || 0
    const trs = rows.length
      ? rows.map((row) => {
          const c = row.bucketValue
          const pct = total ? ((c / total) * 100).toFixed(1) : '0.0'
          const w = total ? (c / total) * 100 : 0
          return h('tr', [
            h('td', row.bucketName),
            h('td', { class: 'num' }, c),
            h('td', { class: 'num' }, pct + '%'),
            h('td', { class: 'dac-bar-cell' }, [
              h('div', { class: 'dac-bar-bg' }, [
                h('div', { class: 'dac-bar-fill', style: { width: w + '%' } })
              ])
            ])
          ])
        })
      : [h('tr', [h('td', { attrs: { colspan: 4 }, style: { color: '#94a3b8' } }, '无数据')])]
    return h('table', { class: 'dac-table' }, [
      h('thead', [
        h('tr', [
          h('th', '名称'),
          h('th', { class: 'num' }, '条数'),
          h('th', { class: 'num' }, '占比'),
          h('th', { class: 'dac-bar-cell' }, '分布')
        ])
      ]),
      h('tbody', trs)
    ])
  }
}

export default {
  name: 'KanbanDeviceAlarmCountStats',
  components: { NxReportHeader, BucketTable },
  data() {
    return {
      loading: false,
      dateRange: null,
      filters: {
        alertType: undefined,
        type: undefined,
        alertStatus: undefined,
        category: undefined
      },
      result: {
        kpi: { totalCount: 0, todayCount: 0, openCount: 0, level1OpenCount: 0 },
        byCategory: [],
        byType: [],
        byAlertType: [],
        byStatus: [],
        matrixCells: []
      },
      categoryOptions: [],
      typeOptions: [],
      alertTypeOptions: [],
      statusOptions: []
    }
  },
  computed: {
    kpi() {
      return this.result.kpi || {}
    },
    byCategory() {
      return this.result.byCategory || []
    },
    byType() {
      return this.result.byType || []
    },
    byAlertType() {
      return this.result.byAlertType || []
    },
    byStatus() {
      return this.result.byStatus || []
    },
    matrix() {
      const cells = this.result.matrixCells || []
      const total = this.kpi.totalCount || 0
      if (!cells.length && !total) {
        return {
          rowKeys: [],
          colKeys: [],
          maxV: 0,
          get: () => 0,
          rowSum: () => 0,
          colSum: () => 0,
          grand: 0
        }
      }
      const rowSet = new Set()
      const colSet = new Set()
      const categoryOrder = this.categoryOptions.map((x) => x.label)
      const levelOrder = this.alertTypeOptions.map((x) => x.label)
      levelOrder.forEach((x) => colSet.add(x))
      cells.forEach((c) => {
        rowSet.add(c.rowKey)
        colSet.add(c.colKey)
      })
      const rowKeys = this.orderKeys([...rowSet], categoryOrder)
      const colKeys = this.orderKeys([...colSet], levelOrder)
      const map = new Map()
      let maxV = 0
      cells.forEach((c) => {
        const v = c.cellValue || 0
        map.set(`${c.rowKey}\t${c.colKey}`, v)
        if (v > maxV) maxV = v
      })
      const get = (r, col) => map.get(`${r}\t${col}`) || 0
      const rowSum = (r) => colKeys.reduce((s, col) => s + get(r, col), 0)
      const colSum = (col) => rowKeys.reduce((s, r) => s + get(r, col), 0)
      return {
        rowKeys,
        colKeys,
        maxV,
        get,
        rowSum,
        colSum,
        grand: total
      }
    }
  },
  created() {
    this.loadDictionaries()
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadDictionaries() {
      Promise.all([
        this.loadDictOptions('base_device_type'),
        this.loadDictOptions('device_alarm_type'),
        this.loadDictOptions('alert_type'),
        this.loadDictOptions('alert_status')
      ]).then(([categories, types, alertTypes, statuses]) => {
        this.categoryOptions = categories
        this.typeOptions = types
        this.alertTypeOptions = alertTypes
        this.statusOptions = this.withLegacyStatus(statuses)
      }).catch(() => {
        this.categoryOptions = []
        this.typeOptions = []
        this.alertTypeOptions = []
        this.statusOptions = this.withLegacyStatus([])
      })
    },
    loadDictOptions(code) {
      return getDictionary({ code }).then((res) => {
        const list = res && res.data && res.data.data
        if (!Array.isArray(list)) return []
        return list
          .filter((item) => item && item.dictKey != null && String(item.dictKey) !== '-1')
          .map((item) => ({
            label: item.dictValue || String(item.dictKey),
            value: String(item.dictKey)
          }))
      })
    },
    withLegacyStatus(statuses) {
      const list = Array.isArray(statuses) ? statuses.slice() : []
      if (!list.some((item) => String(item.value) === '0')) {
        list.unshift({ label: '待确认', value: '0' })
      }
      return list
    },
    orderKeys(all, preferred) {
      const pref = preferred.filter((k) => all.includes(k))
      const rest = all.filter((k) => !preferred.includes(k)).sort()
      return [...pref, ...rest]
    },
    cellClass(v) {
      const maxV = this.matrix.maxV
      if (maxV > 0 && v >= maxV * 0.7 && v > 0) return 'cell-hot'
      if (v > 0) return 'cell-warm'
      return ''
    },
    resetFilters() {
      this.dateRange = null
      this.filters = {
        alertType: undefined,
        type: undefined,
        alertStatus: undefined,
        category: undefined
      }
      this.loadData()
    },
    async loadData() {
      this.loading = true
      try {
        const dr = this.dateRange
        const params = { ...this.filters }
        if (dr && dr.length === 2 && dr[0] != null && dr[1] != null) {
          params.startTime = dr[0]
          params.endTime = dr[1]
        }
        this.result = await fetchDeviceAlarmCountStats(params)
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
